import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { theme } from '../styles/theme';
import { FEATURED_CLUB_COUNT, ClubTile, useFeaturedClubs } from '../hooks/useFeaturedClubs';
import { ReadyClub } from '../utils/featuredClubs';

const cardsForWidth = (width: number) => Math.max(2, Math.min(FEATURED_CLUB_COUNT, Math.floor((width + 14) / 174)));
const viewportRowWidth = () => Math.min(1320, window.innerWidth - (window.innerWidth <= 700 ? 0 : 48));
const RAIL_TRAVEL_MS = 10000;
const MOBILE_STEP_MS = 1200;
const MOBILE_SLIDE_MS = 300;
const smoothStep = (from: number, to: number, value: number) => {
  const t = Math.max(0, Math.min(1, (value - from) / (to - from)));
  return t * t * (3 - 2 * t);
};

/** Mount the actual decoded preload element, rather than asking a second img to load the URL. */
const ClubCrest: React.FC<{ club: ReadyClub }> = ({ club }) => {
  const holder = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const container = holder.current;
    if (!container) return;
    container.appendChild(club.image);
    return () => {
      if (club.image.parentElement === container) container.removeChild(club.image);
    };
  }, [club]);
  return <div className="features-club-crest" ref={holder} aria-hidden="true" />;
};

const ClubCard: React.FC<{
  tile: ClubTile;
  position: number;
}> = React.memo(({ tile, position }) => (
  <li className="features-club-card" data-club-slot={position} data-club-id={tile.current?.id} aria-label={tile.current?.name ?? 'Loading club'}>
      <div className="features-club-front">
        <div className="features-club-content">
        {tile.current ? (
          <>
            <ClubCrest key={tile.current.id} club={tile.current} />
            <span className="features-club-name" title={tile.current.name}>{tile.current.name}</span>
          </>
        ) : (
          <div className="features-club-placeholder" aria-hidden="true">
            <span /><span />
          </div>
        )}
        </div>
      </div>
  </li>
));

/** Existing Features anchor and homepage position, now presenting eligible catalogue clubs. */
export const Features: React.FC = () => {
  const section = useRef<HTMLElement>(null);
  const blurId = 'club-motion-' + useId().replace(/:/g, '');
  const blurPrimitive = useRef<SVGFEGaussianBlurElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const row = useRef<HTMLUListElement>(null);
  const lowerRow = useRef<HTMLUListElement>(null);
  const railOffset = useRef(0);
  const mobileTimeline = useRef({ elapsed: 0, origin: 0 });
  const seenSlots = useRef(new Set<number>());
  const advanceRail = useRef<(direction: number) => void>(() => {});
  const [rowWidth, setRowWidth] = useState(viewportRowWidth);
  const mobile = window.innerWidth <= 700;
  const previousMode = useRef(mobile);
  const cardCount = mobile ? 3 : cardsForWidth(rowWidth);
  const gap = mobile ? rowWidth * .015 : 14;
  const cardWidth = mobile ? rowWidth * .7 : .8 * (rowWidth - gap * (cardCount - 1)) / cardCount;
  const viewportWidth = rowWidth;
  const pitch = cardWidth + gap;
  const centerInset = mobile ? (rowWidth - cardWidth) / 2 : 0;
  const [inView, setInView] = useState(false);
  const [hidden, setHidden] = useState(() => document.hidden);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const active = inView && !hidden && !paused;
  // Every rail slot has unique decoded media; cards recycle beyond the clipped edges.
  const slotsPerRow = mobile ? 3 : Math.ceil(rowWidth / pitch) + 2;
  const poolSlots = mobile ? 3 : slotsPerRow * 2;
  const { tiles, status, replaceOffscreen, retry } = useFeaturedClubs(active, poolSlots);
  const loadedCount = tiles.filter((tile) => tile.current).length;
  const railReady = loadedCount === tiles.length && tiles.length === poolSlots;

  useLayoutEffect(() => {
    const updateWidth = () => setRowWidth(inner.current?.clientWidth || viewportRowWidth());
    updateWidth();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateWidth);
    if (inner.current) observer?.observe(inner.current);
    window.addEventListener('resize', updateWidth);
    return () => { observer?.disconnect(); window.removeEventListener('resize', updateWidth); };
  }, []);

  useLayoutEffect(() => {
    if (previousMode.current !== mobile) {
      railOffset.current = Math.round(railOffset.current) % tiles.length;
      mobileTimeline.current = { elapsed: 0, origin: railOffset.current };
      previousMode.current = mobile;
    }
    const cards = Array.from(inner.current?.querySelectorAll<HTMLElement>('.features-club-card') ?? []);
    const span = mobile ? tiles.length : slotsPerRow;
    const contentWidth = cardWidth;
    const previousPositions = new Map<number, number>();
    let lastBlur = -1;
    const setMotionBlur = (trail: number) => {
      // A uniform half-frame exposure has variance trail² / 12. Approximate
      // that softness with a horizontal Gaussian, capped at a 1.5px trail.
      const deviation = Math.round(Math.min(1.5, Math.max(0, trail)) / Math.sqrt(12) * 100) / 100;
      if (deviation === lastBlur) return;
      lastBlur = deviation;
      blurPrimitive.current?.setAttribute('stdDeviation', `${deviation} 0`);
      [row.current, lowerRow.current].forEach(rail => rail?.style.setProperty('--features-motion-filter', deviation > 0 ? `url(#${blurId})` : 'none'));
    };
    setMotionBlur(0);
    const paint = (replenish = false) => {
      cards.forEach((card) => {
        const index = Number(card.dataset.clubSlot);
        // Wrap only beyond the clipped left edge, to an offscreen position on the right.
        // Mobile needs its left peek to leave completely before wrapping, hence -1.5 slots.
        const wrapInset = mobile ? 1.5 : 1;
        const offset = railReady ? railOffset.current : 0;
        const localIndex = mobile ? index : index % slotsPerRow;
        const reverse = !mobile && index >= slotsPerRow;
        const slot = ((localIndex - offset + wrapInset) % span + span) % span - wrapInset;
        const forwardX = centerInset + slot * pitch;
        const x = reverse ? viewportWidth - cardWidth - forwardX : forwardX;
        const onScreen = x < viewportWidth && x + contentWidth > 0;
        const previousX = previousPositions.get(index);
        if (replenish && previousX !== undefined && Math.abs(x - previousX) > pitch && !onScreen && (previousX >= viewportWidth || previousX + contentWidth <= 0) && seenSlots.current.has(index)) {
          replaceOffscreen(index);
          seenSlots.current.delete(index);
        }
        previousPositions.set(index, x);
        if (previousX !== x) card.style.transform = `translateX(${x}px)`;
        if (card.style.visibility !== 'visible') card.style.visibility = 'visible';
        if (card.getAttribute('aria-hidden') !== String(!onScreen)) card.setAttribute('aria-hidden', String(!onScreen));
        // Preserve the existing mobile seen threshold used by offscreen recycling.
        // It no longer controls the card's appearance or accessibility visibility.
        const hasEnteredReadingArea = !mobile || smoothStep(.05, .92, Math.abs(x - centerInset) / pitch) < .99;
        if (onScreen && hasEnteredReadingArea) seenSlots.current.add(index);
      });
    };
    paint();
    advanceRail.current = (direction) => {
      if (!railReady) return;
      setMotionBlur(0);
      railOffset.current = ((Math.round(railOffset.current) + direction) % span + span) % span;
      if (mobile) mobileTimeline.current = { elapsed: 0, origin: railOffset.current };
      paint(true);
    };
    if (!active || reducedMotion || !railReady) return;
    let previous = performance.now();
    let frame: number;
    const travel = (now: number) => {
      // Use one shared offset so every card travels at exactly the same speed.
      const elapsed = Math.min(Math.max(0, now - previous), 100);
      previous = now;
      let speed = 0;
      if (mobile) {
        // A fixed start-to-start cadence, with a readable hold between horizontal slides.
        // railReady guarantees every incoming slot already contains its decoded image.
        const timeline = mobileTimeline.current;
        timeline.elapsed += elapsed;
        const sinceFirstStep = timeline.elapsed - MOBILE_STEP_MS;
        if (sinceFirstStep >= 0) {
          const steps = Math.floor(sinceFirstStep / MOBILE_STEP_MS);
          const phase = sinceFirstStep % MOBILE_STEP_MS;
          const progress = smoothStep(0, MOBILE_SLIDE_MS, phase);
          // Derivative of the existing smoothStep curve; zero throughout holds.
          const t = Math.min(1, phase / MOBILE_SLIDE_MS);
          speed = phase < MOBILE_SLIDE_MS ? pitch * 6 * t * (1 - t) / MOBILE_SLIDE_MS : 0;
          railOffset.current = (timeline.origin + steps + progress) % span;
        }
      } else {
        speed = (viewportWidth + cardWidth) / RAIL_TRAVEL_MS;
        railOffset.current = (railOffset.current + elapsed * (viewportWidth + cardWidth) / (RAIL_TRAVEL_MS * pitch)) % span;
      }
      // Do not exaggerate the blur after a dropped frame.
      setMotionBlur(speed * Math.min(elapsed, 1000 / 30) / 2);
      paint(true);
      frame = window.requestAnimationFrame(travel);
    };
    frame = window.requestAnimationFrame(travel);
    return () => { window.cancelAnimationFrame(frame); setMotionBlur(0); };
  }, [active, reducedMotion, railReady, loadedCount, tiles.length, cardCount, cardWidth, pitch, gap, rowWidth, viewportWidth, centerInset, mobile, status, replaceOffscreen, blurId, slotsPerRow]);

  useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotion = () => setReducedMotion(motion.matches);
    const updateVisibility = () => setHidden(document.hidden);
    motion.addEventListener('change', updateMotion);
    document.addEventListener('visibilitychange', updateVisibility);
    const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting), { threshold: 0.1 }
    );
    if (observer && section.current) observer.observe(section.current);
    else setInView(true);
    return () => {
      observer?.disconnect();
      motion.removeEventListener('change', updateMotion);
      document.removeEventListener('visibilitychange', updateVisibility);
    };
  }, []);

  const clubRail = (rowIndex: number) => (
    <div className="features-club-viewport" key={rowIndex}>
    <ul
      className={'features-club-row' + (mobile ? ' features-club-mobile' : '')}
      ref={rowIndex === 0 ? row : lowerRow}
      style={{ '--features-club-width': `${cardWidth}px` } as React.CSSProperties}
      aria-label={`Clubs in the 4KMatch catalogue${mobile ? '' : rowIndex === 0 ? ' — top row' : ' — bottom row'}. Arrow keys browse; Space pauses or resumes automatic playback. Automatic playback ${paused ? 'paused' : 'enabled'}.`}
      aria-roledescription="carousel"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === ' ') {
          event.preventDefault();
          if (!event.repeat) setPaused(value => !value);
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          advanceRail.current((event.key === 'ArrowRight' ? 1 : -1) * (rowIndex === 1 ? -1 : 1));
        }
      }}
    >
      {tiles.slice(rowIndex * slotsPerRow, (rowIndex + 1) * slotsPerRow).map((tile, localIndex) => tile.current || status === 'loading' ? (
        <ClubCard key={rowIndex * slotsPerRow + localIndex} position={rowIndex * slotsPerRow + localIndex} tile={tile} />
      ) : null)}
    </ul>
      <div className="features-club-edge features-club-edge-left" data-home-gradient="full" aria-hidden="true" />
      <div className="features-club-edge features-club-edge-right" data-home-gradient="full" aria-hidden="true" />
    </div>
  );

  return (
    <section id="features" ref={section} className="features-clubs" aria-labelledby="features-heading">
      <svg width="0" height="0" aria-hidden="true" focusable="false" style={{ position: 'absolute', pointerEvents: 'none' }}>
        <defs>
          <filter id={blurId} x="-10%" y="0%" width="120%" height="100%" colorInterpolationFilters="sRGB">
            <feGaussianBlur ref={blurPrimitive} stdDeviation="0 0" />
          </filter>
        </defs>
      </svg>
      <div className="features-clubs-inner" ref={inner} style={paused ? { '--home-gradient-state': 'paused' } as React.CSSProperties : undefined}>
        <header className="features-clubs-heading">
          <h2 id="features-heading">Find your club in <span>4KMATCH.</span></h2>
        </header>

        {(loadedCount > 0 || status === 'loading') && <div className="features-club-rails">{Array.from({ length: mobile ? 1 : 2 }, (_, index) => clubRail(index))}</div>}

        <p className="features-clubs-status" role="status">
          {!loadedCount && status === 'loading' ? 'Loading club crests…' : ''}
          {!loadedCount && status === 'empty' ? 'More club crests are on their way.' : ''}
          {!loadedCount && status === 'error' ? 'Club crests are temporarily unavailable.' : ''}
        </p>
        {!loadedCount && (status === 'empty' || status === 'error') && (
          <button type="button" className="features-clubs-retry" onClick={retry}>Try again</button>
        )}
      </div>
      <style>{STYLE}</style>
    </section>
  );
};

const STYLE = `
  #features.features-clubs {
    position: relative; isolation: isolate; overflow: hidden;
    padding: 32px 24px 24px;
    background: #000; color: #fff; font-family: ${theme.fonts.primary};
  }
  #features.features-clubs::before {
    content: ''; position: absolute; inset: 0; z-index: -1; pointer-events: none;
    background: radial-gradient(ellipse at 12% 15%, rgba(255,39,0,.085), transparent 56%),
      radial-gradient(ellipse at 80% 95%, rgba(255,0,168,.07), transparent 55%);
  }
  #features.features-clubs::after {
    content: ''; position: absolute; inset: 0; z-index: -1; pointer-events: none;
    background-image: linear-gradient(rgba(255,255,255,.028) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,.028) 1px, transparent 1px);
    background-size: 72px 72px;
    mask-image: radial-gradient(ellipse, #000 30%, transparent 90%);
    -webkit-mask-image: radial-gradient(ellipse, #000 30%, transparent 90%);
  }
  #features .features-clubs-inner { position: relative; max-width: 1320px; margin: 0 auto; }
  #features .features-clubs-heading { position: relative; text-align: center; padding: 0; margin: 0 auto 28px; }
  #features .features-clubs-heading h2 {
    margin: 0 auto; font-size: 36px;
    font-family: ${theme.fonts.heading}; font-weight: 750; line-height: 1.12; letter-spacing: -.035em;
  }
  #features .features-clubs-heading h2 span {
    display: inline; background: ${theme.colors.gradientPrimary};
    -webkit-background-clip: text; background-clip: text; color: transparent;
  }
  #features .features-club-row {
    position: relative; height: 148.8px; overflow: hidden; touch-action: pan-y pinch-zoom;
    width: 100%; min-width: 0; margin: 0; padding: 0; list-style: none;
  }
  #features .features-club-card {
    position: absolute; top: 0; left: 0; width: var(--features-club-width);
    min-width: 0; height: 100%; will-change: transform;
  }
  #features .features-club-front {
    position: absolute; inset: 0; border: 4px solid #383334; border-radius: 18px;
    box-sizing: border-box;
    box-shadow: 0 2px 14px rgba(0,0,0,.35); overflow: hidden;
  }
  #features .features-club-front {
    background: #FFFFFF; opacity: 1; color: #151823;
  }
  #features .features-club-content {
    display: flex; flex-direction: column; align-items: center; gap: 8px;
    box-sizing: border-box; height: 100%; padding: 17px 12px 12px;
  }
  #features .features-club-crest {
    width: 100%; flex: 1; min-height: 0; display: flex; align-items: center; justify-content: center;
  }
  #features .features-club-crest img { display: block; width: 100%; height: 100%; object-fit: contain; filter: var(--features-motion-filter, none); }
  #features .features-club-name {
    width: 100%; height: 34px; flex: 0 0 34px; display: -webkit-box;
    -webkit-line-clamp: 2; -webkit-box-orient: vertical;
    text-align: center; font-size: 16px; font-weight: 650;
    line-height: 17px; letter-spacing: -.025em; overflow-wrap: anywhere;
    overflow: hidden;
  }
  #features .features-club-rails { display: grid; gap: 16px; min-width: 0; }
  #features .features-club-viewport { position: relative; min-width: 0; isolation: isolate; }
  #features .features-club-edge { position: absolute; top: 0; bottom: 0; width: clamp(24px, 7%, 92px); z-index: 1; pointer-events: none; background: ${theme.colors.gradientPrimary};
    -webkit-mask-image: linear-gradient(var(--edge-direction), #000 0%, #000 20%, transparent 100%), radial-gradient(ellipse 100% 80% at var(--edge-opening) 50%, transparent 35%, #000 100%);
    mask-image: linear-gradient(var(--edge-direction), #000 0%, #000 20%, transparent 100%), radial-gradient(ellipse 100% 80% at var(--edge-opening) 50%, transparent 35%, #000 100%);
    -webkit-mask-composite: source-in; mask-composite: intersect;
  }
  #features .features-club-edge-left { left: 0; --edge-direction: to right; --edge-opening: 100%; --home-flow-delay: -1.46s; }
  #features .features-club-edge-right { right: 0; --edge-direction: to left; --edge-opening: 0%; --home-flow-delay: -3.65s; }
  #features .features-club-placeholder { width: 100%; height: 100%; display: grid; place-items: center; }
  #features .features-club-placeholder span:first-child { width: 64px; height: 76px; border-radius: 40% 40% 48% 48%; background: #f1f1f3; }
  #features .features-club-placeholder span:last-child { width: 66%; height: 10px; border-radius: 6px; background: #f1f1f3; }
  #features .features-clubs-status { margin: 16px 18px 0; color: #aaa; font-size: 16px; text-align: center; }
  #features .features-clubs-status:empty { display: none; }
  #features .features-clubs-retry { border: 1px solid #777; border-radius: 24px; padding: 10px 18px; background: #111; color: #fff; font: inherit; font-size: 16px; cursor: pointer; display: block; margin: 8px auto 0; min-height: 44px; }
  #features button:disabled { opacity: .5; cursor: default; }
  #features button:hover { border-color: #ff3884; color: #fff; }
  #features button:focus-visible, #features .features-club-row:focus-visible { outline: 2px solid #ff3884; outline-offset: 5px; }
  @media (min-width: 701px) {
    #features .features-club-name { display: block; height: auto; flex: 0 0 auto; min-height: 34px; overflow: visible; -webkit-line-clamp: unset; }
    #features .features-club-edge { width: clamp(20px, 5.6%, 73.6px); }
  }
  @media (max-width: 1100px) {
    #features .features-club-row { height: 152px; }
  }
  @media (max-width: 700px) {
    #features.features-clubs { padding: 16px 0 16px; }
    #features .features-clubs-heading { margin-bottom: 24px; padding: 0 16px; }
    #features .features-clubs-heading h2 { font-size: 24px; }
    #features .features-club-row.features-club-mobile { height: 176px; }
    #features .features-club-front { border-width: 3px; border-radius: 14px; }
    #features .features-club-edge { width: 14%; }
    #features .features-club-content { padding: 12px 12px 10px; }
    #features .features-club-name { font-size: 16px; }
  }
  @media (prefers-reduced-motion: reduce) {
    #features .features-club-card { will-change: auto; }
    #features .features-club-crest img { filter: none; }
  }
`;

export default Features;
